import { Verdict } from '@exam-management/graphql';
import {
  CalendarTodayOutlinedIcon,
  CancelOutlinedIcon,
  CheckCircleOutlineRoundedIcon,
  EllipsisText,
  Flex,
  Label,
  LabelColor,
  LocalOfferOutlinedIcon,
  Text,
} from '@shared/components';
import { formatDateTime } from '@shared/utils';

import { type QuestionSubTaskVerdict } from '../../types';

const VERDICT_COLOR = {
  [Verdict.Approved]: LabelColor.SUCCESS,
  [Verdict.Rejected]: LabelColor.ERROR,
};

const VERDICT_ICON = {
  [Verdict.Approved]: <CheckCircleOutlineRoundedIcon color='success' sx={{ fontSize: 16 }} />,
  [Verdict.Rejected]: <CancelOutlinedIcon color='error' sx={{ fontSize: 16 }} />,
};

const VERDICT_TEXT = {
  [Verdict.Approved]: 'เห็นด้วย',
  [Verdict.Rejected]: 'ไม่เห็นด้วย',
};

interface VerdictSubTaskInfoSectionProps {
  isShowAssignee?: boolean;
  assigneeName: string;
  code: string;
  assignedAt: Date;
  submittedAt: Date | null;
  verdict: QuestionSubTaskVerdict;
}

function TaskDetailRow(props: { code: string; assignedAt: Date; submittedAt: Date | null }) {
  const { code, assignedAt, submittedAt } = props;
  return (
    <Flex
      sx={(theme) => ({
        gap: theme.spacing(3),
      })}
    >
      <Flex
        sx={(theme) => ({
          color: theme.palette.text.secondary,
          alignItems: 'center',
          gap: theme.spacing(1),
        })}
      >
        <LocalOfferOutlinedIcon sx={{ fontSize: '16px' }} />
        <Text variant='body2'>Task ID:</Text>
        <Text variant='body2'>{code}</Text>
      </Flex>
      <Flex
        sx={(theme) => ({
          color: theme.palette.text.secondary,
          alignItems: 'center',
          gap: theme.spacing(1),
        })}
      >
        <CalendarTodayOutlinedIcon sx={{ fontSize: '16px' }} />
        <Text variant='body2'>Assigned Date:</Text>
        <Text variant='body2'>{formatDateTime(assignedAt)}</Text>
      </Flex>
      <Flex
        sx={(theme) => ({
          color: theme.palette.text.secondary,
          alignItems: 'center',
          gap: theme.spacing(1),
        })}
      >
        <CalendarTodayOutlinedIcon sx={{ fontSize: '16px' }} />
        <Text variant='body2'>Submitted Date:</Text>
        <Text variant='body2'>{formatDateTime(submittedAt)}</Text>
      </Flex>
    </Flex>
  );
}
export default function VerdictSubTaskInfoSection(props: Readonly<VerdictSubTaskInfoSectionProps>) {
  const { isShowAssignee = false, assigneeName, code, assignedAt, submittedAt, verdict } = props;
  if (!isShowAssignee) {
    return (
      <Flex
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <TaskDetailRow code={code} assignedAt={assignedAt} submittedAt={submittedAt} />
        <Label
          color={VERDICT_COLOR[verdict.verdict as keyof typeof VERDICT_COLOR]}
          startIcon={VERDICT_ICON[verdict.verdict as keyof typeof VERDICT_ICON]}
          sx={(theme) => ({
            borderRadius: theme.shape.rounded(6),
            width: 'auto',
            ...theme.typography.body2Semibold,
          })}
        >
          {VERDICT_TEXT[verdict.verdict as keyof typeof VERDICT_TEXT]}
        </Label>
      </Flex>
    );
  }
  return (
    <Flex sx={(theme) => ({ gap: theme.spacing(3), flexDirection: 'column' })}>
      <Flex
        sx={(theme) => ({
          gap: theme.spacing(2),
          justifyContent: 'space-between',
          alignItems: 'center',
        })}
      >
        <EllipsisText
          variant='body1Semibold'
          sx={(theme) => ({ wordBreak: 'break-all', lineHeight: theme.spacing(5) })}
        >
          {assigneeName}
        </EllipsisText>
        <Label
          color={VERDICT_COLOR[verdict.verdict as keyof typeof VERDICT_COLOR]}
          startIcon={VERDICT_ICON[verdict.verdict as keyof typeof VERDICT_ICON]}
          sx={(theme) => ({
            borderRadius: theme.shape.rounded(6),
            width: 'auto',
            ...theme.typography.body2Semibold,
          })}
        >
          {VERDICT_TEXT[verdict.verdict as keyof typeof VERDICT_TEXT]}
        </Label>
      </Flex>
      <TaskDetailRow code={code} assignedAt={assignedAt} submittedAt={submittedAt} />
    </Flex>
  );
}
